import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Auth, authState } from '@angular/fire/auth';
import { map } from 'rxjs/operators';
import { DataService } from '../services/data';
import { LanguageService } from '../services/language.service';
import { MissionConfigService } from '../services/mission-config.service';
import { HelpContentModule, HelpContext, HelpLang, HelpPrefs } from './help.models';
import { defaultPrefs, helpStorageKey, loadPrefs, safeLocalStorage, savePrefs } from './help-storage';

@Injectable({ providedIn: 'root' })
export class HelpStateService {
  private dataService = inject(DataService);
  private languageService = inject(LanguageService);
  private missionConfig = inject(MissionConfigService);
  private auth = inject(Auth);

  private readonly uid = toSignal(authState(this.auth).pipe(map(user => user?.uid ?? '')), { initialValue: '' });
  readonly churchId = toSignal(this.dataService.churchId$, { initialValue: null });
  private readonly roles = toSignal(this.dataService.userRoles$, { initialValue: [] as string[] });
  private readonly tutorialsMission = toSignal(this.dataService.habilitarTutoriales$, { initialValue: false });
  private readonly bankAccountsFeature = toSignal(this.dataService.mostrarCuentasBancarias$, { initialValue: false });
  private readonly manejarBancos = toSignal(this.dataService.manejarBancos$, { initialValue: false });
  private readonly church = toSignal(this.dataService.dataChurch$, { initialValue: null });
  private readonly churches = toSignal(this.dataService.accessibleChurches$, { initialValue: [] as unknown[] });
  private readonly currentLang = toSignal(this.languageService.currentLang$, { initialValue: 'es' });

  readonly lang = computed<HelpLang>(() => (this.currentLang() === 'it' ? 'it' : 'es'));

  readonly context = computed<HelpContext>(() => ({
    roles: this.roles(),
    tutorialsEnabled: this.tutorialsMission() || this.church()?.tutorialesHabilitados === true,
    bankAccountsFeature: this.bankAccountsFeature(),
    manejarBancos: this.manejarBancos(),
    multipleChurches: (this.churches()?.length ?? 0) > 1,
  }));

  /** Sin misión, iglesia y usuario no se lee ni se guarda nada. */
  readonly storageKey = computed(() => {
    const missionId = this.missionConfig.getMissionId();
    const churchId = this.churchId();
    const uid = this.uid();
    return missionId && churchId && uid ? helpStorageKey(missionId, churchId, uid) : null;
  });

  readonly prefs = signal<HelpPrefs>(defaultPrefs());
  readonly panelOpen = signal(false);
  /** Cambia cada vez que cambia la iglesia o el usuario. */
  readonly identityVersion = signal(0);

  private contentPromise: Promise<HelpContentModule> | null = null;

  constructor() {
    effect(() => {
      const key = this.storageKey();
      untracked(() => {
        this.prefs.set(key ? loadPrefs(safeLocalStorage(), key) : defaultPrefs());
        this.panelOpen.set(false);
        this.identityVersion.update(v => v + 1);
      });
    });
  }

  updatePrefs(change: (prefs: HelpPrefs) => HelpPrefs): void {
    const next = change(this.prefs());
    this.prefs.set(next);
    const key = this.storageKey();
    if (key) savePrefs(safeLocalStorage(), key, next);
  }

  loadContent(): Promise<HelpContentModule> {
    if (!this.contentPromise) {
      this.contentPromise = import('./help-content').catch(err => {
        this.contentPromise = null;
        throw err;
      });
    }
    return this.contentPromise;
  }
}
